import { Fragment, type FormEvent, useMemo, useState } from "react";

import { ErrorNote } from "@/components/AppShell";
import MeasurementField from "@/components/MeasurementField";
import {
  CLARITY_LABELS,
  EXTRA_PARAMS,
  FULL_PARAMS,
  PARAMS,
  PH_CHLORINE_INTERFERENCE_PPM,
  PH_NEUTRALISED_LABEL,
  QUICK_PARAMS,
  type ParamKey,
  phTestChlorine,
} from "@/lib/params";
import { type ParamInputs, type ReadingDraft, takenAtIso } from "@/lib/readingDraft";
import type { Clarity, EntryMode } from "@/types";

/** Every param, in the order the full panel asks for them. */
const PANEL_ORDER: ParamKey[] = [...FULL_PARAMS, ...EXTRA_PARAMS];

interface Props {
  /** Seeds the fields once, and is the baseline an edit diffs against. */
  initial: ReadingDraft;
  submitLabel: string;
  pendingLabel: string;
  /** Null when the draft can be saved, otherwise why it can't — shown under the button. */
  blockedReason: (draft: ReadingDraft, shown: ParamKey[]) => string | null;
  onSubmit: (draft: ReadingDraft, shown: ParamKey[]) => Promise<void>;
  /** Used only when something non-Error is thrown; API errors carry their own. */
  errorFallback: string;
  /** Decides which fields are worth asking for. See `shown` below. */
  vesselType?: "pool" | "hot_tub";
}

/**
 * The one measurement-entry form, shared by logging and editing.
 *
 * The mode toggle chooses how much to *ask* for, not how much may be stored:
 * any param that already holds a value stays on screen whatever the mode, so a
 * stored figure can never hide somewhere the user cannot see or clear it.
 */
export default function ReadingForm({
  initial,
  submitLabel,
  pendingLabel,
  blockedReason,
  onSubmit,
  errorFallback,
  vesselType,
}: Props) {
  const [mode, setMode] = useState<EntryMode>(initial.mode);
  const [values, setValues] = useState<ParamInputs>(initial.values);
  const [takenAt, setTakenAt] = useState(initial.takenAt);
  const [clarity, setClarity] = useState<Clarity | "">(initial.clarity);
  const [notes, setNotes] = useState(initial.notes);
  const [phNeutralised, setPhNeutralised] = useState(initial.phNeutralised);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const populated = useMemo(
    () => PANEL_ORDER.filter((key) => (initial.values[key] ?? "").trim() !== ""),
    [initial],
  );

  // Filter pressure assumes a gauge and a backwash valve — a sand or DE filter.
  // A hot tub runs a cartridge you pull out and rinse: nothing to read. The
  // warning that consumes it is already gated (ADR 0015), so asking for the
  // measurement here would invite a number that can never mean anything. Still
  // shown if a value is already present, because hiding a field with data in it
  // is how an edit silently drops it.
  const shown: ParamKey[] = useMemo(() => {
    const asked = mode === "quick" ? QUICK_PARAMS : PANEL_ORDER;
    const relevant = PANEL_ORDER.filter(
      (key) => key !== "filter_pressure_psi" || vesselType !== "hot_tub",
    );
    return relevant.filter((key) => asked.includes(key) || populated.includes(key));
  }, [mode, populated, vesselType]);

  // The R-0007 checkbox appears when it can change an answer, and stays on
  // screen once ticked.
  //
  // Shown only above the interference threshold, rather than always: below it
  // the phenol red reagent's own neutraliser copes, the server's guard never
  // fires, and a permanently visible "did you neutralise?" on the daily quick
  // check is a control that means nothing 99 days out of 100 — which is how you
  // teach someone to ignore it. On this pool it is visible every time anyway,
  // because at CYA 157 the FC target itself is 15.5-17.5.
  //
  // The "already ticked" arm mirrors the rule the panel above follows for
  // populated params: a fact that is stored has to stay somewhere the user can
  // see and un-tick it. Without it, editing a neutralised reading down to a low
  // FC would strand a `true` nobody could reach.
  // Shown when it could matter; hidden only when this reading's own chlorine
  // rules interference out, or when nothing relevant has been entered at all.
  //
  // Keyed on FC alone before, which made it unreachable on a pH-only retest —
  // the reading you take after adding acid names no FC, so there was no way to
  // record that you had neutralised the sample. Keying on pH alone would have
  // been the mirror of that bug: FC is the first field in the panel, so the
  // control would vanish for anyone filling the form top to bottom.
  //
  // A measured-low sanitiser is the one case that genuinely settles it: below
  // the threshold the reagent's own neutraliser copes and the server's guard
  // never fires, so asking is noise.
  const sanitiser = phTestChlorine(values.fc, values.cc);
  const chlorineRulesItOut = sanitiser !== null && sanitiser <= PH_CHLORINE_INTERFERENCE_PPM;
  const relevant = (values.ph ?? "").trim() !== "" || sanitiser !== null;
  const showPhNeutralised = phNeutralised || (relevant && !chlorineRulesItOut);

  const draft: ReadingDraft = { mode, values, takenAt, clarity, notes, phNeutralised };
  // "When" belongs to this form, so its validity is this form's problem: an
  // emptied or half-entered box names no instant, and a reading has to have one.
  const blocked =
    takenAtIso(takenAt) === null
      ? "Pick when this reading was taken."
      : blockedReason(draft, shown);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await onSubmit(draft, shown);
    } catch (err) {
      setError(err instanceof Error ? err.message : errorFallback);
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <div
        role="group"
        aria-label="How much did you test?"
        className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-(--color-surface-sunken) p-1"
      >
        {(["quick", "full"] as EntryMode[]).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setMode(option)}
            aria-pressed={mode === option}
            className={[
              "rounded-lg py-2.5 text-sm font-medium",
              mode === option
                ? "bg-(--color-surface-raised) text-(--color-ink) shadow-sm"
                : "text-(--color-ink-muted)",
            ].join(" ")}
          >
            {option === "quick" ? "Quick check" : "Full panel"}
          </button>
        ))}
      </div>

      <div className="card divide-y divide-(--color-border-subtle) px-4">
        {shown.map((key) => (
          <Fragment key={key}>
            <MeasurementField
              meta={PARAMS[key]}
              value={values[key] ?? ""}
              onChange={(next) => setValues((prev) => ({ ...prev, [key]: next }))}
            />
            {/* Directly under pH, because it qualifies that reading and nothing
                else. Floated to the bottom of the form it would read as another
                thing about the water rather than about the test. */}
            {key === "ph" && showPhNeutralised && (
              <div className="flex items-start gap-3 py-3">
                <input
                  id="field-ph-neutralised"
                  type="checkbox"
                  checked={phNeutralised}
                  onChange={(e) => setPhNeutralised(e.target.checked)}
                  aria-describedby="hint-ph-neutralised"
                  className="mt-0.5 size-5 shrink-0 accent-(--color-reagent-phenol)"
                />
                <div>
                  <label htmlFor="field-ph-neutralised" className="text-sm font-medium">
                    {PH_NEUTRALISED_LABEL}
                  </label>
                  <p id="hint-ph-neutralised" className="mt-1 text-xs text-(--color-ink-faint)">
                    Chlorine is over {PH_CHLORINE_INTERFERENCE_PPM} ppm, which bleaches
                    phenol red and makes pH read wrong in either direction. Tick this if you
                    put a drop of R-0007 in the sample first — otherwise no pH dose can be
                    suggested from this reading.
                  </p>
                </div>
              </div>
            )}
          </Fragment>
        ))}
      </div>

      <div className="card mt-4 space-y-4 p-4">
        <div>
          <label htmlFor="taken-at" className="text-sm font-medium">
            When
          </label>
          <input
            id="taken-at"
            type="datetime-local"
            value={takenAt}
            onChange={(e) => setTakenAt(e.target.value)}
            className="field figure mt-2"
          />
        </div>

        <div>
          <label htmlFor="clarity" className="text-sm font-medium">
            Water clarity
          </label>
          <select
            id="clarity"
            value={clarity}
            onChange={(e) => setClarity(e.target.value as Clarity | "")}
            className="field mt-2"
          >
            <option value="">Not noted</option>
            {Object.entries(CLARITY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="notes" className="text-sm font-medium">
            Notes
          </label>
          <textarea
            id="notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything worth remembering about this test."
            className="field mt-2 resize-y"
          />
        </div>
      </div>

      {error && (
        <div className="mt-4">
          <ErrorNote message={error} />
        </div>
      )}

      <button
        type="submit"
        disabled={saving || blocked !== null}
        className="btn btn-primary mt-5 w-full"
      >
        {saving ? pendingLabel : submitLabel}
      </button>
      {blocked && (
        <p className="mt-2 text-center text-xs text-(--color-ink-faint)">{blocked}</p>
      )}
    </form>
  );
}
